import React from 'react';
import { Play, Search, GitCompare } from 'lucide-react';

interface DeadlineClausesToolbarProps {
  filters: {
    urgency: string;
    category: string;
    keyword: string;
  };
  onFilterChange: (filters: { urgency: string; category: string; keyword: string }) => void;
  onRunAnalysis: () => void;
  isRunning: boolean;
  hasFindings: boolean;
  onCompare: () => void;
}

export const DeadlineClausesToolbar: React.FC<DeadlineClausesToolbarProps> = ({
  filters,
  onFilterChange,
  onRunAnalysis,
  isRunning,
  hasFindings,
  onCompare,
}) => {
  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="deadline-toolbar">
      {/* Run Analysis button */}
      <button
        onClick={onRunAnalysis}
        disabled={isRunning}
        className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:bg-primary-300 text-sm font-medium"
        data-testid="run-deadline-analysis-button"
      >
        {isRunning ? (
          <>
            <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
            Running...
          </>
        ) : (
          <>
            <Play className="h-4 w-4 mr-2" />
            Run Analysis
          </>
        )}
      </button>

      {/* Compare button — only show when we have findings */}
      {hasFindings && (
        <button
          onClick={onCompare}
          className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
          data-testid="compare-deadlines-button"
        >
          <GitCompare className="h-4 w-4 mr-2" />
          Compare Contracts
        </button>
      )}

      {/* Filters — only show when we have findings */}
      {hasFindings && (
        <>
          {/* Urgency dropdown */}
          <select
            value={filters.urgency}
            onChange={(e) =>
              onFilterChange({ ...filters, urgency: e.target.value })
            }
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500"
            data-testid="deadline-urgency-filter"
          >
            <option value="">All Urgencies</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>

          {/* Category dropdown */}
          <select
            value={filters.category}
            onChange={(e) =>
              onFilterChange({ ...filters, category: e.target.value })
            }
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500"
            data-testid="deadline-category-filter"
          >
            <option value="">All Categories</option>
            <option value="immediate">Immediate</option>
            <option value="hours">Hours</option>
            <option value="days_numeric">Days (Numeric)</option>
            <option value="days_written">Days (Written)</option>
            <option value="weeks">Weeks</option>
            <option value="months">Months</option>
            <option value="years">Years</option>
            <option value="no_later_than">No Later Than</option>
            <option value="recurring">Recurring</option>
            <option value="calendar_date">Calendar Date</option>
          </select>

          {/* Keyword search */}
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={filters.keyword}
              onChange={(e) =>
                onFilterChange({ ...filters, keyword: e.target.value })
              }
              placeholder="Search clauses..."
              className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500 w-48"
              data-testid="deadline-keyword-filter"
            />
          </div>
        </>
      )}
    </div>
  );
};
